import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import { Card, CardBody, Button, Typography, Alert } from "@material-tailwind/react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import RHFPasswordInput from "components/password-input/rhf-password-input";
import RHFInput from "components/form/rhf-input";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiFetch, fetcherFull } from "utils/api";
import TotpSetupDialog from "components/dialog/totp-setup-dialog";
import Image from "components/image";
import logo from "assets/img/logos/gobby-logo-FIN_Stacked-BL-wTag-.png";
import Trademark from "components/Trademark";

const validationSchema = Yup.object().shape({
    password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        // See resetconfirm.js: verbatim passwords (#1097) mean this is the
        // only thing rejecting an all-whitespace credential.
        .matches(/\S/, "Password cannot be only spaces")
        .required("Password is required"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),
});

// The form lives in a child component rendered only once the token check
// has passed (the recipe P5 child-component shape, adapted to a page):
// RHF caches defaultValues on the first render of the component that
// calls useForm, and the account email arrives asynchronously from that
// check, so a useForm at the top of InviteAccept would run during the
// loading screen and freeze email at "". Mounting the form after
// verification means defaultValues see the resolved email. Module
// scope is required, not stylistic: a component type defined inside the
// parent would be recreated each render, remounting the form and
// dropping field values whenever the parent's TOTP dialog state changes.
function InviteAcceptForm({ inviteEmail, passwordInputRef, onSubmit }) {
    const methods = useForm({
        mode: "onChange",
        resolver: yupResolver(validationSchema),
        defaultValues: {
            email: inviteEmail,
            password: "",
            confirmPassword: "",
        },
    });
    // Destructure the formState flags up front (P1): formState is a
    // subscription proxy and an inline `isValid && isDirty` short-circuits
    // before isDirty while isValid is false, dropping the subscription.
    const { isValid, isDirty } = methods.formState;

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className="px-0 space-y-4">
                    {inviteEmail && (
                        <RHFInput
                            name="email"
                            label="Email"
                            autoComplete="username"
                            readOnly
                        />
                    )}
                    <RHFPasswordInput
                        name="password"
                        label="Password"
                        showStrength={true}
                        autoComplete="new-password"
                        required
                        ref={passwordInputRef}
                    />
                    <RHFPasswordInput
                        name="confirmPassword"
                        label="Confirm Password"
                        showStrength={false}
                        autoComplete="new-password"
                        required
                    />
                    <div className="flex justify-center mt-6">
                        <Button
                            type="submit"
                            variant="filled"
                            size="lg"
                            className="btn btn--cta w-full text-2xl"
                            disabled={!(isValid && isDirty)}
                        >
                            Set Password
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
}

export default function InviteAccept() {
    const { token } = useParams();
    const navigate = useNavigate();
    const passwordInputRef = useRef(null);

    const [isTokenValid, setIsTokenValid] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [inviteEmail, setInviteEmail] = useState("");
    const [showTOTPSetupDialog, setShowTOTPSetupDialog] = useState(false);
    const [totpSecret, setTotpSecret] = useState("");
    const [totpSecret32, setTotpSecret32] = useState("");
    const [totpEmail, setTotpEmail] = useState("");
    const [totpError, setTotpError] = useState("");
    const [totpLoading, setTotpLoading] = useState(false);

    // Validate token on mount.
    useEffect(() => {
        if (token) {
            validateToken();
        }
    }, [token]);

    useEffect(() => {
        if (isTokenValid && passwordInputRef.current) {
            passwordInputRef.current.focus();
        }
    }, [isTokenValid]);

    const validateToken = async () => {
        setIsLoading(true);
        setErrorMessage("");

        try {
            const response = await apiFetch(`/v2/password-reset/${encodeURIComponent(token)}`, {
                method: "GET",
                // Asks a newer backend for the account email; older ones
                // keep replying 204, so both ends ship in any order.
                headers: { Accept: "application/json" },
            });

            if (response.status === 204) {
                // Older backend: token valid, no account email to show.
                setIsTokenValid(true);
            } else {
                // Newer backend returns the account email so the form can
                // carry a username for password managers (#1112). A malformed
                // body degrades to "no email", never to "invalid token".
                const data = await response.json().catch(() => null);
                setInviteEmail(data?.email || "");
                setIsTokenValid(true);
            }
        } catch (error) {
            setIsTokenValid(false);
            const displayError = error.message ||
                "Invalid or expired token. Please request a new invite link.";
            setErrorMessage(displayError);
            toast.error("Invalid invite token");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordSubmit = async (values) => {
        // Verbatim, not sanitiseInput'd — see #1097 / gobby-app/gobbyapi#466.
        const password = values.password;
        setShowTOTPSetupDialog(true);
        setTotpError("");
        setTotpLoading(true);
        let passwordSet = false;
        try {
            const passwordResetResponse = await fetcherFull(
                `/v2/password-reset/${encodeURIComponent(token)}`,
                "POST",
                { password },
            );
            passwordSet = true;
            if (passwordResetResponse?.email) {
                setTotpEmail(passwordResetResponse.email);
            }
            const response = await apiFetch("/v2/auth/setup-totp", {
                method: "GET",
                credentials: "include",
            });
            const data = await response.json();
            if (!data.secret32) {
                throw new Error("Failed to get TOTP setup data");
            }
            setTotpSecret(data.secret || "");
            setTotpSecret32(data.secret32);
        } catch (error) {
            if (passwordSet) {
                // The password was changed but the 2FA secret could not be
                // fetched. The invite token is consumed, so don't strand the
                // user: let them enable 2FA later in settings.
                setShowTOTPSetupDialog(false);
                toast.success(
                    "Password set. You can enable two-factor authentication later in your account settings."
                );
                navigate("/");
            } else {
                setTotpError(error.message || "Failed to set password");
                toast.error("Failed to set password. Please try again.");
            }
        } finally {
            setTotpLoading(false);
        }
    };

    const handleTotpVerify = async (code, secret) => {
        const response = await apiFetch("/v2/auth/setup-totp", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ secret, code }),
        });
        if (response.status !== 204) {
            throw new Error("Invalid authentication code");
        }
    };

    const handleTotpSetupSuccess = () => {
        setShowTOTPSetupDialog(false);
        toast.success("Account setup complete!");
        navigate("/");
    };

    const handleTotpSetupSkip = () => {
        setShowTOTPSetupDialog(false);
        toast.success("Password set! You can set up TOTP later in your account settings.");
        navigate("/");
    };

    const handleTotpSetupClose = () => {
        setShowTOTPSetupDialog(false);
        toast.info("You can set up TOTP later in your account settings.");
    };

    return (
        <>
            <div className="flex flex-col min-w-screen min-h-screen bg-white">
                <div className="basis-0 h-[250px] md:basis-1/3 pl-3 pt-3">
                    <Image
                        className="sm:w-[250px] w-[0px]"
                        src={logo}
                        rounded={false}
                        raised={false}
                        alt="Gobby Logo"
                    />
                </div>
                <div className="flex min-h-fit justify-center">
                    <Card
                        className="card-inner filter-none sm:p-8 sm:pt-6 p-4"
                        shadow={false}
                    >
                        <Typography className="font-display text-center md:text-xl lg:content-center md:mt-2 text-primary">
                            Welcome to Gobby!
                            <Trademark />
                        </Typography>
                        <CardBody className="card-inner-body font-body my-4 pb-2">
                            {isLoading ? (
                                <Typography className="text-center py-8">
                                    Validating invite...
                                </Typography>
                            ) : !isTokenValid ? (
                                <Alert color="red" className="mb-4">
                                    {errorMessage}
                                </Alert>
                            ) : (
                                <>
                                    <Typography className="text-center text-primary mb-6">
                                        Set your password to join your
                                        organisation
                                    </Typography>
                                    <InviteAcceptForm
                                        inviteEmail={inviteEmail}
                                        passwordInputRef={passwordInputRef}
                                        onSubmit={handlePasswordSubmit}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </div>
            <TotpSetupDialog
                open={showTOTPSetupDialog}
                onClose={handleTotpSetupClose}
                onSetupSuccess={handleTotpSetupSuccess}
                onSetupSkip={handleTotpSetupSkip}
                secretCode={totpSecret}
                secret32={totpSecret32}
                userEmail={totpEmail}
                onVerify={handleTotpVerify}
                isLoading={totpLoading}
                setupError={totpError}
            />
        </>
    );
}
